// Prompt Filter Service - Handles all filtering and sorting logic
import { PromptData } from '../hooks/useProjectData';

export interface FilterOptions {
  contentType: 'all' | 'image' | 'video' | 'music' | 'sfx' | 'narration' | 'character' | 'others';
  status: 'all' | 'draft' | 'completed';
  sortBy: 'date-desc' | 'date-asc' | 'type' | 'title' | 'sequence';
}

export interface FilterStats {
  total: number;
  image: number;
  video: number;
  music: number;
  sfx: number;
  narration: number;
  character: number;
  others: number;
  draft: number;
  completed: number;
}

export class PromptFilterService {
  /**
   * Filter prompts by content type based on first word in title
   */
  static filterByContentType(prompts: PromptData[], contentType: string): PromptData[] {
    if (contentType === 'all') return prompts;
    
    return prompts.filter(prompt => {
      const title = prompt.title || '';
      const firstWord = title.split(' ')[0]?.toLowerCase();
      
      // Map content types to first words in titles
      switch (contentType) {
        case 'image':
          return firstWord === 'image';
        case 'video':
          return firstWord === 'video';
        case 'music':
          return firstWord === 'music';
        case 'sfx':
          return firstWord === 'sound' || firstWord === 'sfx';
        case 'narration':
          return firstWord === 'narration' || firstWord === 'voice';
        case 'character':
          return firstWord === 'character';
        case 'others':
          // Others = anything that doesn't match the above patterns
          return !['image', 'video', 'music', 'sound', 'sfx', 'narration', 'voice', 'character'].includes(firstWord);
        default:
          return true;
      }
    });
  }

  /**
   * Filter prompts by status (draft/completed)
   */
  static filterByStatus(prompts: PromptData[], status: string): PromptData[] {
    if (status === 'all') return prompts;
    
    return prompts.filter(prompt => {
      const isDraft = !prompt.generatedPrompt || prompt.generatedPrompt.trim() === '';
      
      if (status === 'draft') return isDraft;
      if (status === 'completed') return !isDraft;
      
      return true;
    });
  }

  /**
   * Sort prompts by specified criteria
   */
  static sortPrompts(prompts: PromptData[], sortBy: string): PromptData[] {
    const sorted = [...prompts];
    
    switch (sortBy) {
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      
      case 'type':
        return sorted.sort((a, b) => {
          const typeA = (a.userSelections as any)?.outputType || 'zzz';
          const typeB = (b.userSelections as any)?.outputType || 'zzz';
          return typeA.localeCompare(typeB);
        });
      
      case 'title':
        return sorted.sort((a, b) => {
          const titleA = a.title || 'Untitled';
          const titleB = b.title || 'Untitled';
          return titleA.localeCompare(titleB);
        });
      
      case 'sequence':
        return sorted.sort((a, b) => {
          const parsedA = this.parseTitle(a.title || '');
          const parsedB = this.parseTitle(b.title || '');
          
          // First by content type
          if (parsedA.contentType !== parsedB.contentType) {
            return parsedA.contentType.localeCompare(parsedB.contentType);
          }
          
          // Then by sequence number within same type
          return parsedA.sequence - parsedB.sequence;
        });
      
      default:
        return sorted;
    }
  }

  /**
   * Parse title to extract content type and sequence number
   */
  private static parseTitle(title: string): { contentType: string; sequence: number } {
    // Match patterns like "Image 10:", "Video 3:", "Sound 2:", etc.
    const match = title.match(/^(\w+)\s+(\d+):/);
    
    if (match) {
      return {
        contentType: match[1].toLowerCase(),
        sequence: parseInt(match[2])
      };
    }
    
    // Fallback for titles without numbers - put them at end
    const firstWord = title.split(' ')[0]?.toLowerCase() || 'zzz';
    return {
      contentType: firstWord,
      sequence: 999 // Put unnumbered items at end
    };
  }

  /**
   * Apply all filters and sorting
   */
  static applyFilters(prompts: PromptData[], options: FilterOptions): PromptData[] {
    let filtered = prompts;
    
    // Apply content type filter
    filtered = this.filterByContentType(filtered, options.contentType);
    
    // Apply status filter
    filtered = this.filterByStatus(filtered, options.status);
    
    // Apply sorting
    filtered = this.sortPrompts(filtered, options.sortBy);
    
    return filtered;
  }

  /**
   * Get filter statistics based on title analysis
   */
  static getFilterStats(prompts: PromptData[]): FilterStats {
    const stats: FilterStats = {
      total: prompts.length,
      image: 0,
      video: 0,
      music: 0,
      sfx: 0,
      narration: 0,
      character: 0,
      others: 0,
      draft: 0,
      completed: 0
    };

    prompts.forEach(prompt => {
      const title = prompt.title || '';
      const firstWord = title.split(' ')[0]?.toLowerCase();
      const isDraft = !prompt.generatedPrompt || prompt.generatedPrompt.trim() === '';
      
      // Count by content type based on first word in title
      if (firstWord === 'image') {
        stats.image++;
      } else if (firstWord === 'video') {
        stats.video++;
      } else if (firstWord === 'music') {
        stats.music++;
      } else if (firstWord === 'sound' || firstWord === 'sfx') {
        stats.sfx++;
      } else if (firstWord === 'narration' || firstWord === 'voice') {
        stats.narration++;
      } else if (firstWord === 'character') {
        stats.character++;
      } else {
        stats.others++;
      }
      
      // Count by status
      if (isDraft) {
        stats.draft++;
      } else {
        stats.completed++;
      }
    });

    return stats;
  }

  /**
   * Get context-aware filter statistics based on current content type filter
   * This shows how many completed/draft prompts exist within the selected content type
   */
  static getContextualFilterStats(prompts: PromptData[], currentContentType: string): FilterStats {
    // First get base stats for all prompts (for content type counts)
    const baseStats = this.getFilterStats(prompts);
    
    // If showing all types, return base stats
    if (currentContentType === 'all') {
      return baseStats;
    }
    
    // Filter to only the selected content type
    const filteredByType = this.filterByContentType(prompts, currentContentType);
    
    // Now calculate status counts ONLY for the filtered type
    let draftCount = 0;
    let completedCount = 0;
    
    filteredByType.forEach(prompt => {
      const isDraft = !prompt.generatedPrompt || prompt.generatedPrompt.trim() === '';
      if (isDraft) {
        draftCount++;
      } else {
        completedCount++;
      }
    });
    
    // Return stats with content type counts from base, but contextual status counts
    return {
      ...baseStats,
      total: filteredByType.length,
      draft: draftCount,
      completed: completedCount
    };
  }

  /**
   * Get content type icon for UI
   */
  static getContentTypeIcon(contentType: string): string {
    const iconMap: Record<string, string> = {
      image: 'faImage',
      video: 'faVideo', 
      music: 'faMusic',
      sfx: 'faMicrophone',
      narration: 'faMicrophone',
      character: 'faUser',
      others: 'faEllipsisH',
      all: 'faList'
    };
    
    return iconMap[contentType] || 'faEllipsisH';
  }

  /**
   * Get content type display label
   */
  static getContentTypeLabel(contentType: string): string {
    const labelMap: Record<string, string> = {
      all: 'All',
      image: 'Images',
      video: 'Videos',
      music: 'Music',
      sfx: 'Sound FX',
      narration: 'Narration',
      character: 'Characters',
      others: 'Others'
    };
    
    return labelMap[contentType] || 'Unknown';
  }
}
